import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  styled,
  Switch,
  switchClasses,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { PracticeInfo } from "../../../redux/slice/practiceSlice";
import {
  selectIsValueFoundInToken,
  setSelectedModule,
  useAuthSlice,
} from "../../../redux/slice/authSlice";
import { fullName, hideElement, sortNames, getCategoryDisplayLabel } from "../../../utils/Utility";
import {
  IVR_STATUS,
  PRODUCT_CATEGORY,
  USER_ROLES,
} from "../../../constants/applicationConstants";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { fetchPracticeProductsData } from "../../../redux/slice/practice/practiceProductSlice";
import CustomRadioButton from "../../common/CustomRadioButton";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import CustomStyledRadio from "./CustomStyledRadio";
import { SwitchTextTrack } from "../product/Product";
import { getPatientListByPracticeId, resetPatientListByPracticeId, usePatientReducer } from "../../../redux/slice/patient/patientSlice";
import { checkAncilliaryAgreementsForPractice } from "../../../redux/slice/productAgreement/createAgreementSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import { useModalStyles } from "../../common/useModalStyles";

interface IVRPracticeSelectionProps {
  open: boolean;
  onClose: () => void;
  practiceByUserRole?: PracticeInfo[] | null | undefined;
  patientData?: { from: string; patientId: number | null } | null;
  practiceId?: number | null;
}

type Option = {
  value: string;
  label: string;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const IVRPracticeSelectionPopup: React.FC<IVRPracticeSelectionProps> = ({
  open,
  onClose,
  practiceByUserRole,
  patientData,
  practiceId,
}) => {
  const { control, handleSubmit, reset, setValue, getValues, watch } = useForm({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { tokens, paperSx, headerSx, closeIconSx, bodySx, footerSx, primaryBtnSx, cancelBtnSx } = useModalStyles();
  const dispatch = useAppDispatch();
  const [message, setMessage] = useState("");
  const { navigate } = useNavigation();
  const { userInfo } = useAuthSlice();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const { patientList } = usePatientReducer();
  const ivrEmailNotificationOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];
  const canAccessIvrEmailSetting = useSelector((state: RootState) =>
    selectIsValueFoundInToken(
      "canAccessIVRActions",
      IVR_STATUS.EMAIL_SETTINGS
    )(state)
  );
  const [ hasAncilliaryAgreements, setHasAncilliaryAgreements ] = useState(false);

  //mount
  useEffect(() => {
    dispatch(resetPatientListByPracticeId());
    if (practiceId) {
      setValue("practice", practiceId);
      fetchPatientsList(practiceId.toString());
    };
    setValue("ivrEmailNotification", true);
  }, []);

  const practiceOptions = useMemo(() => {
    if (userInfo.userRole === USER_ROLES.ROLE_PRACTICEADMIN) {
      let options: Option[] = [];
      if (practiceByUserRole) {
        setValue("practiceId", practiceByUserRole[0]?.practiceId);
        practiceByUserRole?.map((practice) => {
          options.push({
            value: practice.practiceId,
            label: practice.practiceName,
          });
        });
      }
      return options;
    } else {
      let options: Option[] = [];
      if (practiceByUserRole) {
        const sortedPracticeInfo: PracticeInfo[] = sortNames(
          practiceByUserRole,
          "practiceName"
        );
        sortedPracticeInfo?.map((practice) => {
          options.push({
            value: practice.practiceId,
            label: practice.practiceName,
          });
        });
      }
      return options;
    }
  }, [practiceByUserRole]);

  const onSubmit = async (data: any) => {
    if (data.practice) {
      const response = await dispatch(fetchPracticeProductsData(data.practice));
      // const isExecuted = response.payload.data.some(
      //   (product: any) => product.agreementStatus === "EXECUTED"
      // );
      // if (!isExecuted) {
      if (
        !response ||
        !response.payload ||
        !response.payload.data ||
        response.payload.data.length === 0
      ) {
        setMessage(
          // practiceId
          //   ? "Could not find any valid Product Agreements for the selected practice by patient."
          //   : 
          "Could not find any valid Product Agreements for the associated practice."
        );
      } else {
        setMessage("");
        navigate("/ivr", {
          state: {
            practiceId: data.practice,
            productCategory: data.productCategory,
            patientData: patientData
              ? { from: patientData?.from, patientId: patientData?.patientId || getValues('patientId') }
              : null,
            ivrEmailNotification: data.ivrEmailNotification,
          },
        });
      }
    }

    dispatch(setSelectedModule("IVR")); 
  };

  const handleClose = () => {
    onClose();
    reset({});
    setMessage("");
  };

  const productCategoryOptions = useMemo(() => {
    // CS-1728 - Replacing static label to dynamic constant
    setValue('productCategory', PRODUCT_CATEGORY.ADVANCED);
    return [
      { value: PRODUCT_CATEGORY.ADVANCED, label: getCategoryDisplayLabel(allCodeTablesValuesMap, PRODUCT_CATEGORY.ADVANCED) },
    ];
  }, [hasAncilliaryAgreements]); 
  
  const SwitchTextTrack = styled(Switch)({
    width: 80,
    height: 48,
    padding: 8,
    [`& .${switchClasses.switchBase}`]: {
      padding: 11,
      color: "#ff6a00",
    },
    [`& .${switchClasses.thumb}`]: {
      width: 26,
      height: 26,
      backgroundColor: "#fff",
    },
    [`& .${switchClasses.track}`]: {
      background:
        "linear-gradient(to right,rgb(161, 161, 161),rgb(84, 84, 84))",
      opacity: "1 !important",
      borderRadius: 20,
      position: "relative",
      "&:before, &:after": {
        display: "inline-block",
        position: "absolute",
        top: "50%",
        width: "50%",
        transform: "translateY(-50%)",
        color: "#fff",
        textAlign: "center",
        fontSize: "0.75rem",
        fontWeight: 500,
      },
      "&:before": {
        content: '"ON"',
        left: 4,
        opacity: 0,
      },
      "&:after": {
        content: '"OFF"',
        right: 4,
      },
    },
    [`& .${switchClasses.checked}`]: {
      [`&.${switchClasses.switchBase}`]: {
        color: "#185a9d",
        transform: "translateX(32px)",
        "&:hover": {
          backgroundColor: "rgba(24,90,257,0.08)",
        },
      },
      [`& .${switchClasses.thumb}`]: {
        backgroundColor: "#fff",
      },
      [`& + .${switchClasses.track}`]: {
        background: "linear-gradient(to right,rgb(13, 212, 169),rgb(1, 112, 57))",
        "&:before": {
          opacity: 1,
        },
        "&:after": {
          opacity: 0,
        },
      },
    },
  });

  const patientsOptions = React.useMemo(() => {
    let options: { label: string; value: string; patientCode: string }[] = [];
    patientList?.map((option: any) => {
      options.push({
        label: fullName(
          "",
          option.firstName,
          option.middleName,
          option.lastName
        ) + " - " + option.birthDate,
        value: option.patientId,
        patientCode: option.patientCode,
      });
    });
    return options;
  }, [patientList]);

  const fetchPatientsList = async (practiceId: string) => {
      await dispatch(getPatientListByPracticeId({ practiceId: practiceId }));
    };

  useEffect(() => {
    if(practiceId || getValues('practice')) {
      const practiceIdNumber = practiceId || getValues('practice')
      checkAncilliaryAgreements(practiceIdNumber)
    }
  }, [practiceId, watch('practice')]);

  const checkAncilliaryAgreements = async (practiceIdNumber: number) => {
    const response = await dispatch(checkAncilliaryAgreementsForPractice(practiceIdNumber));
    setHasAncilliaryAgreements(response?.payload || false);
  };

  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
        PaperProps={{
          sx: paperSx(isMobile),
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(15,23,42,0.45)",
            backdropFilter: "blur(3px)",
          },
        }}
        aria-describedby="alert-dialog-slide-description"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Box sx={headerSx}>
            <Box sx={{ flex: 1, pr: 4 }}>
              <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                Create IVR
              </Typography>

            </Box>
          </Box>

          <Box sx={bodySx}>
            <Box>
              <Grid container spacing={2}>
                {message && (
                  <Box>
                    <Typography sx={{ color: "red", mt: 1.7, ml: 2, display: "block", wordWrap: "break-word", fontWeight: "bold" }}>
                      {message}
                    </Typography>
                  </Box>
                )}
                <Grid item md={12} xs={12}></Grid>
                {!practiceId && (
                  <Grid item md={12} xs={12}>
                    <CustomDropdownField
                      name={"practice"}
                      control={control}
                      options={practiceOptions}
                      label="Practice"
                      rules={{
                        required: "Please Select a practice to continue.",
                      }}
                      onChangeSelect={() => {
                        setMessage("");
                        fetchPatientsList(getValues('practice'));
                      }}
                    />
                  </Grid>
                )}
                {!patientData?.patientId &&
                  (<Grid item xs={12} md={12}>
                    <CustomDropdownField
                      name={`patientId`}
                      control={control}
                      options={patientsOptions}
                      label="Patients"
                      rules={{
                        required: "Please Select a patient to continue.",
                      }}
                      disabled={!getValues('practice')}
                    />
                  </Grid>)
                }
                {/* <Grid item md={12} xs={12}>
                  <CustomStyledRadio
                    controllerName={`productCategory`}
                    control={control}
                    options={productCategoryOptions}
                    // CS-1728 - Replacing static label to dynamic constant
                    defaultValue={PRODUCT_CATEGORY.ADVANCED}
                    isDisabled={!hasAncilliaryAgreements}
                  />
                </Grid> */}
                <Grid
                  container
                  mt={2}
                  style={{
                    display: `${hideElement(!canAccessIvrEmailSetting)}`,
                  }}
                >
                  <Grid item md={12} xs={12}>
                    <Controller
                      name={"ivrEmailNotification"}
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <FormControlLabel
                            control={
                              <SwitchTextTrack
                                {...field}
                                name={field.name}
                                checked={field.value}
                                size="medium"
                                onChange={(e) => {
                                  field.onChange(e.target.checked);
                                  setValue(
                                    "ivrEmailNotification",
                                    e.target.checked
                                  );
                                }}
                              />
                            }
                            label={" Do you want to send email notifications?"}
                            labelPlacement="start"
                          />
                        </>
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={footerSx}>
            <Grid
              container
              justifyContent="flex-end"
              sx={{ flexWrap: "wrap" }}
              spacing={1}
            >
              <Grid item xs={12} sm="auto">
                <Box
                  component="button"
                  type="submit"
                  sx={{
                    ...primaryBtnSx,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.8,
                    px: 3,
                    py: 1.2,
                    border: "none",
                    cursor: "pointer",
                    color: "#fff",
                    width: "100%",
                  }}
                >
                  <AddCircleOutlineOutlined sx={{ fontSize: "1.1rem" }} />
                  Create IVR
                </Box>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleClose()}
                  sx={{
                    ...cancelBtnSx,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 3,
                    py: 1.2,
                    border: "1px solid",
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  Cancel
                </Box>
              </Grid>
            </Grid>
          </Box>
        </form>
      </Dialog>
    </>
  );
};

export default IVRPracticeSelectionPopup;
